import { useEffect, useRef, useCallback, useState } from 'react';
import { io, Socket } from 'socket.io-client';
import { useAuthStore } from '@stores/authStore';

interface ChatWebSocketOptions {
  onNewSession?: (session: any) => void;
  onMessage?: (data: { sessionId: string; message: any }) => void;
  onTyping?: (data: { sessionId: string; userId: string; isTyping: boolean }) => void;
  onSessionEnded?: (data: { sessionId: string; reason?: string }) => void;
  /** A queued chat was accepted by an agent: it leaves everyone else's queue. */
  onSessionTaken?: (data: { sessionId: string; agentId: string }) => void;
  /** A chat was transferred to this agent. */
  onChatTransferred?: (session: any) => void;
}

class ChatWebSocketService {
  private socket: Socket | null = null;
  private reconnectAttempts = 0;
  // The rooms this agent is in, re-joined after every reconnect: a dropped
  // socket used to come back deaf to the chat that was open.
  private joined = new Set<string>();

  connect(tenantId: string, userId: string, token: string) {
    if (this.socket?.connected) {
      return;
    }

    // Connect to channel-gateway chat namespace
    const wsUrl = import.meta.env.VITE_CHANNEL_WS_URL || 'https://dev-channel-gateway.centraone.ai';

    this.socket = io(`${wsUrl}/chat`, {
      // A function, not an object: Socket.IO calls it on every (re)connect, so
      // a reconnect after a token refresh presents the CURRENT access token.
      // channel-gateway rejects an agent socket without a valid one.
      auth: (cb) =>
        cb({
          tenantId,
          userId,
          userType: 'agent',
          token: useAuthStore.getState().token || token,
        }),
      transports: ['websocket', 'polling'],
      // Keep trying: giving up after five attempts left the console silently
      // on polling for the rest of the shift.
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 10000,
    });

    this.socket.on('connect', () => {
      console.log('[ChatWS] Connected to chat gateway');
      this.reconnectAttempts = 0;
      for (const sessionId of this.joined) this.socket?.emit('join_session', { sessionId });
    });

    this.socket.on('connected', (data) => {
      console.log('[ChatWS] Received connected event:', data);
    });

    this.socket.on('disconnect', (reason) => {
      console.log('[ChatWS] Disconnected:', reason);
    });

    this.socket.on('connect_error', (error) => {
      console.error('[ChatWS] Connection error:', error.message);
      this.reconnectAttempts++;
    });

    return this.socket;
  }

  disconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
  }

  getSocket(): Socket | null {
    return this.socket;
  }

  isConnected(): boolean {
    return this.socket?.connected ?? false;
  }

  joinSession(sessionId: string) {
    this.joined.add(sessionId);
    if (this.socket?.connected) {
      this.socket.emit('join_session', { sessionId });
    }
  }

  leaveSession(sessionId: string) {
    this.joined.delete(sessionId);
    if (this.socket?.connected) {
      this.socket.emit('leave_session', { sessionId });
    }
  }

  sendMessage(sessionId: string, content: string, type = 'text') {
    if (this.socket?.connected) {
      this.socket.emit('send_message', { sessionId, content, type });
    }
  }

  sendTyping(sessionId: string, isTyping: boolean) {
    if (this.socket?.connected) {
      this.socket.emit('typing', { sessionId, isTyping });
    }
  }
}

// Singleton instance
export const chatWebSocketService = new ChatWebSocketService();

// React hook for chat WebSocket
export function useChatWebSocket(options: ChatWebSocketOptions = {}) {
  const { user, token } = useAuthStore();
  const [isConnected, setIsConnected] = useState(chatWebSocketService.isConnected());
  const optionsRef = useRef(options);

  // Update options ref without triggering reconnect
  useEffect(() => {
    optionsRef.current = options;
  }, [options]);

  const tenantId = user?.tenantId;

  // Connect once per signed-in agent; the connection outlives the page.
  useEffect(() => {
    if (!user?.id || !token || !tenantId) return;
    if (!chatWebSocketService.isConnected()) {
      chatWebSocketService.connect(tenantId, user.id, token);
    }
  }, [user?.id, token, tenantId]);

  // Every mounted page subscribes for itself and unsubscribes when it goes.
  // Subscribing only on the first mount sent events to a page that had
  // already unmounted once the agent navigated away and back.
  useEffect(() => {
    const socket = chatWebSocketService.getSocket();
    if (!socket) return;

    const handlers: Array<[string, (...args: any[]) => void]> = [
      ['connect', () => setIsConnected(true)],
      ['disconnect', () => setIsConnected(false)],
      ['new_waiting_session', (data) => optionsRef.current.onNewSession?.(data)],
      ['new_message', (data) => optionsRef.current.onMessage?.(data)],
      ['typing', (data) => optionsRef.current.onTyping?.(data)],
      ['session_ended', (data) => optionsRef.current.onSessionEnded?.(data)],
      ['session_taken', (data) => optionsRef.current.onSessionTaken?.(data)],
      ['chat_transferred', (data) => optionsRef.current.onChatTransferred?.(data)],
    ];
    for (const [event, handler] of handlers) socket.on(event, handler);
    setIsConnected(socket.connected);

    return () => {
      for (const [event, handler] of handlers) socket.off(event, handler);
    };
  }, [user?.id, token, tenantId]);

  const joinSession = useCallback((sessionId: string) => {
    chatWebSocketService.joinSession(sessionId);
  }, []);

  const leaveSession = useCallback((sessionId: string) => {
    chatWebSocketService.leaveSession(sessionId);
  }, []);

  const sendMessage = useCallback((sessionId: string, content: string) => {
    chatWebSocketService.sendMessage(sessionId, content);
  }, []);

  const sendTyping = useCallback((sessionId: string, isTyping: boolean) => {
    chatWebSocketService.sendTyping(sessionId, isTyping);
  }, []);

  return {
    isConnected,
    joinSession,
    leaveSession,
    sendMessage,
    sendTyping,
  };
}
